/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from './styles.module.scss';
import { SubmitReviewButton } from '@/features/reviews/components/SubmitReviewButton/SubmitReviewButton';
import { useLabels } from '@/hooks/useLabels';
import { Divider, Text, View } from '@az/starc-ui';
import React from 'react';
import { RatingDistributions } from './RatingDistributions/RatingDistributions';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ReviewStatistics } from './ReviewStatistics/ReviewStatistics';
import { useSingleReviewStatistic } from '@/features/reviews/api/getReviewStatistics';
import { LoadingOverview } from './LoadingOverview/LoadingOverview';
import { formatNumber } from '../../utils/formatNumber';

const labelMap = {
  writeAReview: 'label_reviews_writeAReview',
  writeAReviewPascalCase: 'label_reviews_writeAReviewCTA',
  reviews: 'label_reviews',
  recommendations: 'label_reviews_recommendations',
  recommendThis: 'label_reviews_recommendThisProduct',
};

export const ReviewsOverview = () => {
  const labels = useLabels(labelMap);
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  const { data: productDetails } = useProductDetails({ enabled: false });
  const product = productDetails?.product;
  const skuNumber = productDetails?.product?.itemId ?? '';

  const { data: reviewStatistic, isLoading } = useSingleReviewStatistic({
    skuNumber,
    enabled: !!skuNumber,
  });

  if (isLoading) return <LoadingOverview />;

  return (
    <View>
      <View className={styles.summary}>
        <View className={styles.overview}>
          <View className={styles.heading}>
            {reviewStatistic ? <ReviewStatistics reviewStatistic={reviewStatistic} /> : null}
            <Text variant="main-body" color="600" className={styles.reviews}>
              {`${formatNumber(Number(reviewStatistic?.totalReviewCount))} ${labels.reviews}`}
            </Text>
          </View>
          <View className={styles.summaryDetailsContainer}>
            <View className={styles.summaryDetails}>
              <Text variant="main-body" weight="bold" className={styles.reviewersRecommend}>
                {`${formatNumber(Number(reviewStatistic?.recommendPercentage))}% ${
                  labels.recommendThis
                }`}
              </Text>
              <Text variant="small-body" className={styles.recommendations}>
                {`${formatNumber(Number(reviewStatistic?.recommendedCount))} ${
                  labels.recommendations
                }`}
              </Text>
            </View>
            <View className={styles.mdWriteReviewButton}>
              <SubmitReviewButton
                productId={product?.itemId}
                productImage={product?.itemImageUrl}
                productTitle={product?.itemDescription}
                pageType="ProductDetail"
                label={
                  <Text variant="small-body" weight="bold" className={styles.smallBody}>
                    {labels.writeAReviewPascalCase}
                  </Text>
                }
                variant="pill-secondary"
              />
            </View>
          </View>
        </View>
        <View.Item className={styles.divider}>
          <Divider vertical />
        </View.Item>
        <RatingDistributions header={isDesktop} />
        <View className={styles.bottomWriteReviewButton}>
          <SubmitReviewButton
            productId={product?.itemId}
            productImage={product?.itemImageUrl}
            productTitle={product?.itemDescription}
            pageType="ProductDetail"
            label={
              <Text variant="small-body" weight="bold" className={styles.smallBody}>
                {labels.writeAReview}
              </Text>
            }
            variant="pill-secondary"
          />
        </View>
      </View>
    </View>
  );
};
